import React, {useContext, useEffect, useState} from "react";
import OverviewTableContainer from "./OverviewTable";
import OverviewPanel from "../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/OverviewPanel";
import {Typography} from "@material-ui/core";
import {getStepData} from "../../../../api/sustainability/allocation/roadUse/stepData";
import { ReactReduxContext, useDispatch, useSelector } from "react-redux";
import SmartTable from "../../../../components/SmartTable/allocation";
import PrevTable from "./PrevTable";
import parseAndFormat from "../../../../components/texts/parseAndFormat";
import parseToFloat from "../../../../components/texts/parseToFloat";
import {useAllocationEditTableStyles} from "../../../../common/styles/allocationStyles";
import {
  allocationTableHeadings,
  allocationPrevTableHeadings,
  allocationSurplusTableHeadings
} from "../shared/tableHeadings";
import { getBalancedData, getMappedData, isNonEmptyArrayOrObject } from "../shared/dataUtils";

const useStyles = useAllocationEditTableStyles;

interface UpdatedDataProps {
  biotemplates?: any[];
  overview?: any[];
  id?: number;
}

interface Props {
  reqByCustomerDisabled: boolean;
  aboveMandateAfterFulfillDisabled: boolean;
  outJointDisabled: boolean;
  surplusStepDisabled: boolean;
  stepTwoData: any[];
  stepThreeData: any[];
  stepFourData: any[];
  stepJointFulfData: any[];
  stepFiveData: any[];
  surplusBiotemplates: any[];
  activeStep: number;
  onUpdateData: (updatedData: UpdatedDataProps, type: string) => void;
  onUpdateRequirement: (requirementData: Requirement) => void;
  updateIsFetching: (value: boolean) => void;
}

interface Requirement {
  surplus_from_offroad: any;
  sub_requirement_advanced_biofuels: any;
  general_requirement: any;
  reserved_quantity: any;
  joint_fulfillment_out: any;
  remaining_quantity: any;
}

interface OverViewParamProps {
  etanol: number;
  etanol_advanced: number;
  fame: number;
  fame_advanced: number;
  hvo_hro: number;
  hvo_hro_advanced: number;
  nafta: number;
  nafta_advanced: number;
  bio_gasoline: number;
  bio_gasoline_advanced: number;
  general_mandate: number;
  advanced_sub_mandate: number;
  above_mandate_req_by_cust: number;
  above_mandate_after_fulfill: number;
  out_joint_fulfillment: number;
  surplus_volumes: number;
}

interface OverViewReqVolParamProps {
  general_mandate: number;
  general_req_vol: number;
  general_rel_vol: number;
  advanced_sub_mandate: number;
  advanced_sub_req_vol: number;
  advanced_sub_rel_vol: number;
  gasoline_req_vol: number;
  gasoline_rel_vol: number;
  above_mandate_req_by_cust: number;
  etanol: number;
  etanol_advanced: number;
  nafta: number;
  nafta_advanced: number;
  bio_gasoline: number;
  bio_gasoline_advanced: number;
  fame: number;
  fame_advanced: number;
  hvo_hro: number;
  hvo_hro_advanced: number;
  above_mandate_after_fulfill: number;
  out_joint_fulfillment: number;
  surplus_volumes: number;
}

interface OverViewProps {
  req_vol: OverViewReqVolParamProps;
  joint_fulfill: OverViewParamProps;
  allocated_LE: OverViewParamProps;
  allocated_15: OverViewParamProps;
  surplus_from_offroad: OverViewParamProps;
  remaining_vol: OverViewParamProps;
}

const createDefaultOverViewState = (): OverViewProps => ({
  req_vol: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    advanced_sub_mandate: 0,
    advanced_sub_req_vol: 0,
    advanced_sub_rel_vol: 0,
    gasoline_req_vol: 0,
    gasoline_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    etanol: 0,
    etanol_advanced: 0,
    nafta: 0,
    nafta_advanced: 0,
    bio_gasoline: 0,
    bio_gasoline_advanced: 0,
    fame: 0,
    fame_advanced: 0,
    hvo_hro: 0,
    hvo_hro_advanced: 0,
    above_mandate_after_fulfill: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0,
  },
  joint_fulfill: {
    etanol: 0,
    etanol_advanced: 0,
    fame: 0,
    fame_advanced: 0,
    hvo_hro: 0,
    hvo_hro_advanced: 0,
    nafta: 0,
    nafta_advanced: 0,
    bio_gasoline: 0,
    bio_gasoline_advanced: 0,
    general_mandate: 0,
    advanced_sub_mandate: 0,
    above_mandate_req_by_cust: 0,
    above_mandate_after_fulfill: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0,
  },
  allocated_LE: {
    etanol: 0,
    etanol_advanced: 0,
    fame: 0,
    fame_advanced: 0,
    hvo_hro: 0,
    hvo_hro_advanced: 0,
    nafta: 0,
    nafta_advanced: 0,
    bio_gasoline: 0,
    bio_gasoline_advanced: 0,
    general_mandate: 0,
    advanced_sub_mandate: 0,
    above_mandate_req_by_cust: 0,
    above_mandate_after_fulfill: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0,
  },
  allocated_15: {
    etanol: 0,
    etanol_advanced: 0,
    fame: 0,
    fame_advanced: 0,
    hvo_hro: 0,
    hvo_hro_advanced: 0,
    nafta: 0,
    nafta_advanced: 0,
    bio_gasoline: 0,
    bio_gasoline_advanced: 0,
    general_mandate: 0,
    advanced_sub_mandate: 0,
    above_mandate_req_by_cust: 0,
    above_mandate_after_fulfill: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0,
  },
  surplus_from_offroad: {
    etanol: 0,
    etanol_advanced: 0,
    fame: 0,
    fame_advanced: 0,
    hvo_hro: 0,
    hvo_hro_advanced: 0,
    nafta: 0,
    nafta_advanced: 0,
    bio_gasoline: 0,
    bio_gasoline_advanced: 0,
    general_mandate: 0,
    advanced_sub_mandate: 0,
    above_mandate_req_by_cust: 0,
    above_mandate_after_fulfill: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0,
  },
  remaining_vol: {
    etanol: 0,
    etanol_advanced: 0,
    fame: 0,
    fame_advanced: 0,
    hvo_hro: 0,
    hvo_hro_advanced: 0,
    nafta: 0,
    nafta_advanced: 0,
    bio_gasoline: 0,
    bio_gasoline_advanced: 0,
    general_mandate: 0,
    advanced_sub_mandate: 0,
    above_mandate_req_by_cust: 0,
    above_mandate_after_fulfill: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0,
  }
});

const EditableTable: React.FC<Props> = ({
                                          reqByCustomerDisabled,
                                          aboveMandateAfterFulfillDisabled,
                                          outJointDisabled,
                                          surplusStepDisabled,
                                          stepTwoData,
                                          stepThreeData,
                                          stepFourData,
                                          stepJointFulfData,
                                          stepFiveData,
                                          surplusBiotemplates,
                                          activeStep,
                                          onUpdateData,
                                          onUpdateRequirement,
                                          updateIsFetching,
                                        }) => {
  const classes = useStyles();
  const [overViewData, setOverViewData] = useState<OverViewProps>(createDefaultOverViewState());
  const [responseOverViewData, setResponseOverViewData] = useState<OverViewProps>(createDefaultOverViewState());
  const [newData, setNewData] = useState([]);
  const [stepData, setStepData] = useState([]);
  const [stepDataId, setStepDataId] = useState<number|null>(null);
  const [stepThreesData, setStepThreesData] = useState([]);
  const [stepFoursData, setStepFoursData] = useState([]);
  const [stepSurplusData, setStepSurplusData] = useState([]);
  const [stepFivesData, setStepFivesData] = useState([]);
  const [stepJointFulfsData, setStepJointFulfsData] = useState([]);
  const [stepSixsData, setStepSixsData] = useState([]);
  const dispatch = useDispatch();
  const updateReqVol = (newValues: Partial<OverViewReqVolParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      req_vol: { ...prevData.req_vol, ...newValues },
    }));
  };
  const roaduseAllocatedQuantityByStep = useContext(ReactReduxContext).store.getState().sustainability.roaduseAllocation;
  // console.log("QS")
  // console.log(roaduseAllocatedQuantityByStep)

  const updateJointFulfillVol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      joint_fulfill: { ...prevData.joint_fulfill, ...newValues },
    }));
  };

  const updateAllocatedLEVol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      allocated_LE: { ...prevData.allocated_LE, ...newValues },
    }));
  };

  const updateAllocated15Vol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      allocated_15: { ...prevData.allocated_15, ...newValues },
    }));
  };

  const updateSurplusFromOffroadVol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      surplus_from_offroad: { ...prevData.surplus_from_offroad, ...newValues },
    }));
  };


  const getAllocated = (row: any) => parseAndFormat(row.allocated_quantity, 2);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>, row: any) => {
    const { value } = event.target;
    const prevData = { ...row };
    const inputValue = value.replace(/[^0-9.,]/g, "").replace(/,/g, ".");
    const formattedAllocated = inputValue === "" ? 0 : parseAndFormat(inputValue, 0, true);
    const newData = { ...row, allocated_quantity: formattedAllocated };
    // Assuming you are updating some state here
    setNewData(prevRows => {
      const updatedRowsCopy = [...prevRows];
      // @ts-ignore
      const rowIndex = updatedRowsCopy.findIndex(r => r.id === row.id);

      if (rowIndex !== -1) {
        // @ts-ignore
        updatedRowsCopy[rowIndex] = newData;
      }

      return updatedRowsCopy;
    });
  };

  const handleOnBlur = (event: React.ChangeEvent<HTMLInputElement>, row: any[]) => {
    handleOnChange(event, row);
  };

  const getData = (data: any[], activeStep: number) => {
      return data;
  };

  const fetchBiotemplateData = async (activeStep: number, currentStepData: any[]) => {
    const response = await getStepData(activeStep);
    if (response) {
      if ("data" in response) {
        setStepData(response.data);
        if ("id" in response.data) {
          setStepDataId(response.data.id);
        }

        if ("req_vol" in response.data) {
          updateReqVol(response.data.req_vol);
          setResponseOverViewData(prevData => ({
            ...prevData,
            req_vol: { ...prevData.req_vol, ...response.data.req_vol },
          }));
        }
        if ("joint_fulfill" in response.data) {
          updateJointFulfillVol(response.data.joint_fulfill);
          setResponseOverViewData(prevData => ({
            ...prevData,
            joint_fulfill: { ...prevData.joint_fulfill, ...response.data.joint_fulfill },
          }));
        }
        if ("allocated_LE" in response.data) {
          updateAllocatedLEVol(response.data.allocated_LE);
          setResponseOverViewData(prevData => ({
            ...prevData,
            allocated_LE: { ...prevData.allocated_LE, ...response.data.allocated_LE },
          }));
        }
        if ("allocated_15" in response.data) {
          updateAllocated15Vol(response.data.allocated_15);
          setResponseOverViewData(prevData => ({
            ...prevData,
            allocated_15: { ...prevData.allocated_15, ...response.data.allocated_15 },
          }));
        }
        if ("surplus_from_offroad" in response.data) {
          updateSurplusFromOffroadVol(response.data.surplus_from_offroad);
          setResponseOverViewData(prevData => ({
            ...prevData,
            surplus_from_offroad: { ...prevData.surplus_from_offroad, ...response.data.surplus_from_offroad },
          }));
        }

        if ( "table_from_step_surplus_volume" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_surplus_volume)) {
          if (Array.isArray(response.data.table_from_step_surplus_volume)) {
            setStepSurplusData(response.data.table_from_step_surplus_volume);
          } else {
            setStepSurplusData(Object.values(response.data.table_from_step_surplus_volume));
          }
        }

        if ( "table_from_step_three" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_three)) {
          if (Array.isArray(response.data.table_from_step_three)) {
            setStepThreesData(response.data.table_from_step_three);
          } else {
            setStepThreesData(Object.values(response.data.table_from_step_three));
          }
        }

        if ( "table_from_step_four" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_four)) {
          if (Array.isArray(response.data.table_from_step_four)) {
            setStepFoursData(response.data.table_from_step_four);
          } else {
            setStepFoursData(Object.values(response.data.table_from_step_four));
          }
        }

        if ( "table_from_step_five" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_five)) {
          if (Array.isArray(response.data.table_from_step_five)) {
            setStepFivesData(response.data.table_from_step_five);
          } else {
            setStepFivesData(Object.values(response.data.table_from_step_five));
          }
        }

        if ( "table_from_step_joint_fulf" in response.data &&
            isNonEmptyArrayOrObject(response.data.table_from_step_joint_fulf) &&
            !outJointDisabled
        ) {
          if (Array.isArray(response.data.table_from_step_joint_fulf)) {
            setStepJointFulfsData(response.data.table_from_step_joint_fulf);
          } else {
            setStepJointFulfsData(Object.values(response.data.table_from_step_joint_fulf));
          }
        }

        if ( "table_from_step_six" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_six)) {
          if (Array.isArray(response.data.table_from_step_six)) {
            setStepSixsData(response.data.table_from_step_six);
          } else {
            setStepSixsData(Object.values(response.data.table_from_step_six));
          }
        }

        let mappedData;

        if (activeStep === 1 && "table_from_step_surplus_volume" in response.data && !surplusStepDisabled) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_surplus_volume);
        } else if (activeStep === 2 && "table_from_step_three" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_three);
        } else if (activeStep === 3 && "table_from_step_four" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_four);
        } else if (activeStep === 4 && "table_from_step_five" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_five);
        } else if (activeStep === 5 && "table_from_step_joint_fulf" in response.data && !outJointDisabled) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_joint_fulf);
        } else if (activeStep === 6 && "table_from_step_six" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_six);
        } else {
          mappedData = currentStepData;
        }
        const calculatedData = await calculateMappedData(activeStep, mappedData);

        // @ts-ignore
        setNewData(calculatedData);
      } else {
        setStepData([]);
      }
    } else {
      alert("Some error occured");
    }
  };

  const calculateMappedData = async (activeStep: number, data: any[]) => {
    let newCalculatedData = data;

    // Define the steps and the corresponding allocation data
    const steps = [
      roaduseAllocatedQuantityByStep.biotemplatesInAllocationStepSurplusVol,
      roaduseAllocatedQuantityByStep.biotemplatesInAllocationStepTwo,
      roaduseAllocatedQuantityByStep.biotemplatesInAllocationStepThree,
      roaduseAllocatedQuantityByStep.biotemplatesInAllocationStepFour,
      roaduseAllocatedQuantityByStep.biotemplatesInAllocationStepJointFulf,
      roaduseAllocatedQuantityByStep.biotemplatesInAllocationStepFive
    ];

    // Filter out the current active step
    const filteredSteps = steps.filter((_, index) => index !== activeStep - 1 && index > 0);

    // Apply the balance operation to each filtered step
    // for (const stepData of filteredSteps) {
    //   if (stepData) {
    //     newCalculatedData = await getBalancedData(newCalculatedData, stepData);
    //   }
    // }

    // Remove items with available_quantity < 1
    // for (const item of newCalculatedData) {
    //     if (parseToFloat(item.allocated_quantity) > 0) {
    //         item.available_quantity = parseToFloat(item.available_quantity) + parseToFloat(item.allocated_quantity);
    //     }
    // }

    newCalculatedData = newCalculatedData.filter(item => item.available_quantity >= 0);

    return newCalculatedData;
  };

  const updateDataTotals = (responseOverViewData: OverViewProps, newData: any[]) => {

    const dataTotals: OverViewProps = JSON.parse(JSON.stringify(responseOverViewData));

    const in_joint_fulfill_advanced = parseToFloat(dataTotals.joint_fulfill.advanced_sub_mandate.toString(), 2);
    const in_joint_fulfill_general = parseToFloat(dataTotals.joint_fulfill.general_mandate.toString(), 2);

    let surplus_advanced_mandate = activeStep == 1 ? 0 : parseToFloat(dataTotals.surplus_from_offroad.advanced_sub_mandate.toString());
    let surplus_general_mandate = activeStep == 1 ? 0 : parseToFloat(dataTotals.surplus_from_offroad.general_mandate.toString());

    const advanced_req_vol = parseToFloat(dataTotals.req_vol.advanced_sub_req_vol.toString(), 2);
    let allocated_LE_advanced_sub_mandate = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.advanced_sub_mandate.toString()) : 0;
    let allocated_15_advanced_sub_mandate = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.advanced_sub_mandate.toString()) : 0;

    const general_req_vol = parseToFloat(dataTotals.req_vol.general_req_vol.toString(), 2);
    let allocated_LE_general_sub_mandate = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.general_mandate.toString()) : 0;
    let allocated_15_general_sub_mandate = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.general_mandate.toString()) : 0;

    const above_mandate_req_by_cust = parseToFloat(dataTotals.req_vol.above_mandate_req_by_cust.toString());
    let allocated_LE_above_mandate_req_by_cust = parseToFloat(dataTotals.allocated_LE.above_mandate_req_by_cust.toString());
    let allocated_15_above_mandate_req_by_cust = parseToFloat(dataTotals.allocated_15.above_mandate_req_by_cust.toString());

    const above_mandate_after_fulfill_req_vol = parseToFloat(dataTotals.req_vol.above_mandate_after_fulfill.toString());
    let allocated_LE_above_mandate_after_fulfill = parseToFloat(dataTotals.allocated_LE.above_mandate_after_fulfill.toString());
    let allocated_15_above_mandate_after_fulfill = parseToFloat(dataTotals.allocated_15.above_mandate_after_fulfill.toString());

    const out_joint_fulfillment_req_vol = outJointDisabled ? 0 : parseToFloat(dataTotals.req_vol.out_joint_fulfillment.toString());
    let allocated_15_out_joint_fulfillment = (activeStep == 5 && !outJointDisabled) ? 0 : parseToFloat(dataTotals.allocated_15.out_joint_fulfillment.toString());
    let allocated_LE_out_joint_fulfillment = (activeStep == 5 && !outJointDisabled) ? 0 : parseToFloat(dataTotals.allocated_LE.out_joint_fulfillment.toString());
      if (activeStep !== 5 && outJointDisabled) {
          allocated_15_out_joint_fulfillment = 0;
          allocated_LE_out_joint_fulfillment = 0;
      }

    const etanol = parseToFloat(dataTotals.req_vol.etanol.toString());
    let allocated_LE_etanol = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.etanol.toString()) : 0;
    let allocated_15_etanol = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.etanol.toString()) : 0;

    const etanol_advanced = parseToFloat(dataTotals.req_vol.etanol_advanced.toString());
    let allocated_LE_etanol_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.etanol_advanced.toString()) : 0;
    let allocated_15_etanol_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.etanol_advanced.toString()) : 0;

    const nafta = parseToFloat(dataTotals.req_vol.nafta.toString());
    let allocated_LE_nafta = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.nafta.toString()) : 0;
    let allocated_15_nafta = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.nafta.toString()) : 0;

    const nafta_advanced = parseToFloat(dataTotals.req_vol.nafta_advanced.toString());
    let allocated_LE_nafta_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.nafta_advanced.toString()) : 0;
    let allocated_15_nafta_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.nafta_advanced.toString()) : 0;

    const biogasoline = parseToFloat(dataTotals.req_vol.bio_gasoline.toString());
    let allocated_LE_biogasoline = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.bio_gasoline.toString()) : 0;
    let allocated_15_biogasoline = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.bio_gasoline.toString()) : 0;

    const biogasoline_advanced = parseToFloat(dataTotals.req_vol.bio_gasoline_advanced.toString());
    let allocated_LE_biogasoline_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.bio_gasoline_advanced.toString()) : 0;
    let allocated_15_biogasoline_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.bio_gasoline_advanced.toString()) : 0;

    const fame = parseToFloat(dataTotals.req_vol.fame.toString());
    let allocated_LE_fame = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.fame.toString()) : 0;
    let allocated_15_fame = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.fame.toString()) : 0;

    const fame_advanced = parseToFloat(dataTotals.req_vol.fame_advanced.toString());
    let allocated_LE_fame_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.fame_advanced.toString()) : 0;
    let allocated_15_fame_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.fame_advanced.toString()) : 0;

    const hvo_hro = parseToFloat(dataTotals.req_vol.hvo_hro.toString());
    let allocated_LE_hvo_hro = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.hvo_hro.toString()) : 0;
    let allocated_15_hvo_hro = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.hvo_hro.toString()) : 0;

    const hvo_hro_advanced = parseToFloat(dataTotals.req_vol.hvo_hro_advanced.toString());
    let allocated_LE_hvo_hro_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_LE.hvo_hro_advanced.toString()) : 0;
    let allocated_15_hvo_hro_advanced = activeStep > 2 ? parseToFloat(dataTotals.allocated_15.hvo_hro_advanced.toString()) : 0;

    for (const row of newData) {
      if (activeStep === 1 && !surplusStepDisabled) {
        // @ts-ignore
        surplus_advanced_mandate += parseToFloat(row.allocated_quantity);
        surplus_general_mandate += parseToFloat(row.allocated_quantity);
      }

      if (activeStep <= 2 && activeStep !== 1) {
        // @ts-ignore
        if (row.double_counted_norway === "Ja") {
          // @ts-ignore
          allocated_LE_advanced_sub_mandate += parseToFloat(row.allocated_quantity);
          // @ts-ignore
          allocated_15_advanced_sub_mandate += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        allocated_LE_general_sub_mandate += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_general_sub_mandate += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 3) {
        // @ts-ignore
        if (row.double_counted_norway === "Ja") {
          // @ts-ignore
          allocated_LE_general_sub_mandate += (parseToFloat(row.allocated_quantity) * 2 );
        } else {
          // @ts-ignore
          allocated_LE_general_sub_mandate += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        allocated_15_general_sub_mandate += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 4) {
        // @ts-ignore
        allocated_LE_above_mandate_req_by_cust += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_above_mandate_req_by_cust += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 5 && !outJointDisabled) {
        // @ts-ignore
        allocated_LE_out_joint_fulfillment += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_out_joint_fulfillment += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 6) {
        // @ts-ignore
        allocated_LE_above_mandate_after_fulfill += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_above_mandate_after_fulfill += parseToFloat(row.allocated_quantity);
      }

      if (activeStep !== 1) {
        if (row.double_counted_norway === "Nei" && row.biofuel_type === "Etanol") {
          // @ts-ignore
          allocated_LE_etanol += parseToFloat(row.allocated_quantity);
          // @ts-ignore
          allocated_15_etanol += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Ja" && row.biofuel_type === "Etanol") {
          if (activeStep === 3) {
            // @ts-ignore
            allocated_LE_etanol_advanced += parseToFloat(row.allocated_quantity) * 2;
          } else {
            // @ts-ignore
            allocated_LE_etanol_advanced += parseToFloat(row.allocated_quantity);
          }

          // @ts-ignore
          allocated_15_etanol_advanced += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Nei" && row.biofuel_type === "Nafta") {
          // @ts-ignore
          allocated_LE_nafta += parseToFloat(row.allocated_quantity);
          // @ts-ignore
          allocated_15_nafta += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Ja" && row.biofuel_type === "Nafta") {

          if (activeStep === 3) {
            // @ts-ignore
            allocated_LE_nafta_advanced += parseToFloat(row.allocated_quantity) * 2;
          } else {
            // @ts-ignore
            allocated_LE_nafta_advanced += parseToFloat(row.allocated_quantity);
          }

          // @ts-ignore
          allocated_15_nafta_advanced += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Nei" && (row.biofuel_type === "Bio gasoline" || row.biofuel_type === "Biogasoline")) {
          // @ts-ignore
          allocated_LE_biogasoline += parseToFloat(row.allocated_quantity);
          // @ts-ignore
          allocated_15_biogasoline += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Ja" && (row.biofuel_type === "Bio gasoline" || row.biofuel_type === "Biogasoline")) {

          if (activeStep === 3) {
            // @ts-ignore
            allocated_LE_biogasoline_advanced += parseToFloat(row.allocated_quantity) * 2;
          } else {
            // @ts-ignore
            allocated_LE_biogasoline_advanced += parseToFloat(row.allocated_quantity);
          }

          // @ts-ignore
          allocated_15_biogasoline_advanced += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Nei" && row.biofuel_type === "HVO/HRO") {
          // @ts-ignore
          allocated_LE_hvo_hro += parseToFloat(row.allocated_quantity);
          // @ts-ignore
          allocated_15_hvo_hro += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Ja" && row.biofuel_type === "HVO/HRO") {

          if (activeStep === 3) {
            // @ts-ignore
            allocated_LE_hvo_hro_advanced += parseToFloat(row.allocated_quantity) * 2;
          } else {
            // @ts-ignore
            allocated_LE_hvo_hro_advanced += parseToFloat(row.allocated_quantity);
          }

          // @ts-ignore
          allocated_15_hvo_hro_advanced += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Nei" && row.biofuel_type === "FAME") {
          // @ts-ignore
          allocated_LE_fame += parseToFloat(row.allocated_quantity);
          // @ts-ignore
          allocated_15_fame += parseToFloat(row.allocated_quantity);
        }
        // @ts-ignore
        if (row.double_counted_norway === "Ja" && row.biofuel_type === "FAME") {
          if (activeStep === 3) {
            // @ts-ignore
            allocated_LE_fame_advanced += parseToFloat(row.allocated_quantity) * 2;
          } else {
            // @ts-ignore
            allocated_LE_fame_advanced += parseToFloat(row.allocated_quantity);
          }
          // @ts-ignore
          allocated_15_fame_advanced += parseToFloat(row.allocated_quantity);
        }
      }
    }

    dataTotals.req_vol.above_mandate_after_fulfill = above_mandate_after_fulfill_req_vol;

    dataTotals.surplus_from_offroad.advanced_sub_mandate = surplus_advanced_mandate;
    dataTotals.surplus_from_offroad.general_mandate = surplus_general_mandate;

    dataTotals.allocated_LE.advanced_sub_mandate = allocated_LE_advanced_sub_mandate;
    dataTotals.allocated_15.advanced_sub_mandate = allocated_15_advanced_sub_mandate;
    dataTotals.remaining_vol.advanced_sub_mandate = advanced_req_vol - allocated_15_advanced_sub_mandate - in_joint_fulfill_advanced - surplus_advanced_mandate;

    dataTotals.allocated_LE.general_mandate = allocated_LE_general_sub_mandate;
    dataTotals.allocated_15.general_mandate = allocated_15_general_sub_mandate;
    dataTotals.remaining_vol.general_mandate = general_req_vol - allocated_LE_general_sub_mandate - in_joint_fulfill_general - surplus_general_mandate;

    dataTotals.allocated_LE.above_mandate_req_by_cust = allocated_LE_above_mandate_req_by_cust;
    dataTotals.allocated_15.above_mandate_req_by_cust = allocated_15_above_mandate_req_by_cust;
    dataTotals.remaining_vol.above_mandate_req_by_cust = above_mandate_req_by_cust - allocated_15_above_mandate_req_by_cust;

    dataTotals.allocated_LE.above_mandate_after_fulfill = allocated_LE_above_mandate_after_fulfill;
    dataTotals.allocated_15.above_mandate_after_fulfill = allocated_15_above_mandate_after_fulfill;
    dataTotals.remaining_vol.above_mandate_after_fulfill = above_mandate_after_fulfill_req_vol - allocated_15_above_mandate_after_fulfill;

    dataTotals.allocated_LE.out_joint_fulfillment = allocated_LE_out_joint_fulfillment;
    dataTotals.allocated_15.out_joint_fulfillment = allocated_15_out_joint_fulfillment;
    dataTotals.remaining_vol.out_joint_fulfillment = out_joint_fulfillment_req_vol - allocated_15_out_joint_fulfillment;

    dataTotals.allocated_LE.etanol = allocated_LE_etanol;
    dataTotals.allocated_15.etanol = allocated_15_etanol;
    dataTotals.remaining_vol.etanol = etanol - allocated_15_etanol;

    dataTotals.allocated_LE.etanol_advanced = allocated_LE_etanol_advanced;
    dataTotals.allocated_15.etanol_advanced = allocated_15_etanol_advanced;
    dataTotals.remaining_vol.etanol_advanced = etanol_advanced - allocated_15_etanol_advanced;

    dataTotals.allocated_LE.nafta = allocated_LE_nafta;
    dataTotals.allocated_15.nafta = allocated_15_nafta;
    dataTotals.remaining_vol.nafta = nafta - allocated_15_nafta;

    dataTotals.allocated_LE.nafta_advanced = allocated_LE_nafta_advanced;
    dataTotals.allocated_15.nafta_advanced = allocated_15_nafta_advanced;
    dataTotals.remaining_vol.nafta_advanced = nafta_advanced - allocated_15_nafta_advanced;

    dataTotals.allocated_LE.bio_gasoline = allocated_LE_biogasoline;
    dataTotals.allocated_15.bio_gasoline = allocated_15_biogasoline;
    dataTotals.remaining_vol.bio_gasoline = biogasoline - allocated_15_biogasoline;

    dataTotals.allocated_LE.bio_gasoline_advanced = allocated_LE_biogasoline_advanced;
    dataTotals.allocated_15.bio_gasoline_advanced = allocated_15_biogasoline_advanced;
    dataTotals.remaining_vol.bio_gasoline_advanced = biogasoline_advanced - allocated_15_biogasoline_advanced;

    dataTotals.allocated_LE.hvo_hro = allocated_LE_hvo_hro;
    dataTotals.allocated_15.hvo_hro = allocated_15_hvo_hro;
    dataTotals.remaining_vol.hvo_hro = hvo_hro - allocated_15_hvo_hro;

    dataTotals.allocated_LE.hvo_hro_advanced = allocated_LE_hvo_hro_advanced;
    dataTotals.allocated_15.hvo_hro_advanced = allocated_15_hvo_hro_advanced;
    dataTotals.remaining_vol.hvo_hro_advanced = hvo_hro_advanced - allocated_15_hvo_hro_advanced;

    dataTotals.allocated_LE.fame = allocated_LE_fame;
    dataTotals.allocated_15.fame = allocated_15_fame;
    dataTotals.remaining_vol.fame = fame - allocated_15_fame;

    dataTotals.allocated_LE.fame_advanced = allocated_LE_fame_advanced;
    dataTotals.allocated_15.fame_advanced = allocated_15_fame_advanced;
    dataTotals.remaining_vol.fame_advanced = fame_advanced - allocated_15_fame_advanced;

    return dataTotals;
  };

  useEffect(() => {
    let newDataForStep;

    switch (activeStep) {
      case 1:
        newDataForStep = surplusBiotemplates;
        break;
      case 2:
        newDataForStep = stepTwoData;
        break;
      case 3:
        newDataForStep = stepThreeData;
        break;
      case 4:
        newDataForStep = stepFourData;
        break;
      case 5:
        newDataForStep = stepJointFulfData;
        break;
      case 6:
        newDataForStep = stepFiveData;
        break;
      default:
        newDataForStep = [];
        break;
    }
    // @ts-ignore
    setNewData(newDataForStep);
  }, [surplusBiotemplates, stepTwoData, stepThreeData,
    stepFourData, stepJointFulfData, stepFiveData, activeStep]);

  useEffect(() => {
    updateIsFetching(true);
    let currentStepData;
    switch (activeStep) {
      case 1:
        currentStepData = surplusBiotemplates;
        break;
      case 2:
        currentStepData = stepTwoData;
        break;
      case 3:
        currentStepData = stepThreeData;
        break;
      case 4:
        currentStepData = stepFourData;
        break;
      case 5:
        currentStepData = stepJointFulfData;
        break;
      case 6:
        currentStepData = stepFiveData;
        break;
      default:
        currentStepData = [];
        break;
    }

    fetchBiotemplateData(activeStep, currentStepData);
    updateIsFetching(false);
  }, [activeStep]);

  useEffect(() => {
    // @ts-ignore
    const copy = newData.map(item => ({ ...item,  allocated_quantity: parseToFloat(item.allocated_quantity)}));

    // @ts-ignore
    const newArray = {
      // @ts-ignore
      biotemplates: copy.filter(item => item.allocated_quantity > 0).map(item => (
          {id: item.id, available_quantity: item.available_quantity, allocated_quantity: item.allocated_quantity}))
    };
    // @ts-ignore
    onUpdateData(newArray, "bio");

    if (activeStep > 1 && activeStep <= 6) {
      const params: Requirement = {
        surplus_from_offroad: {},
        sub_requirement_advanced_biofuels: {},
        general_requirement: {},
        reserved_quantity: {},
        joint_fulfillment_out: {},
        remaining_quantity: {},
      };

      stepThreesData.forEach(item => {
        // @ts-ignore
        params.sub_requirement_advanced_biofuels[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      stepFoursData.forEach(item => {
        // @ts-ignore
        params.general_requirement[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      stepFivesData.forEach(item => {
        // @ts-ignore
        params.reserved_quantity[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      if (!outJointDisabled) {
        stepJointFulfsData.forEach(item => {
          // @ts-ignore
          params.joint_fulfillment_out[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
        });
      }

      stepSixsData.forEach(item => {
        // @ts-ignore
        params.remaining_quantity[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      const updatedArray = { biotemplates: [] };

      // Create a map for faster lookup
      const newArrayMap = {};
      newArray.biotemplates.forEach(item => {
        newArrayMap[item.id] = item.allocated_quantity;
      });

      // Update newArray based on stepFivesData

      // Add the remaining entries from newArrayMap to updatedArray
      Object.entries(newArrayMap).forEach(([id, allocated_quantity]) => {
        // @ts-ignore
        updatedArray.biotemplates.push({ id: Number(id), allocated_quantity: parseToFloat(String(allocated_quantity)) });
      });

      if (activeStep == 2) {
          updatedArray.biotemplates.forEach(item => {
              // @ts-ignore
              params.sub_requirement_advanced_biofuels[item.id] = item.allocated_quantity;
          });
      } else if (activeStep == 3) {
          updatedArray.biotemplates.forEach(item => {
              // @ts-ignore
              params.general_requirement[item.id] = item.allocated_quantity;
          });
      } else if (activeStep == 4) {
          updatedArray.biotemplates.forEach(item => {
            // @ts-ignore
            params.reserved_quantity[item.id] = item.allocated_quantity;
          });
      } else if (activeStep == 5 && !outJointDisabled) {
          updatedArray.biotemplates.forEach(item => {
              // @ts-ignore
              params.joint_fulfillment_out[item.id] = item.allocated_quantity;
          });
      } else {
          updatedArray.biotemplates.forEach(item => {
              // @ts-ignore
              params.remaining_quantity[item.id] = item.allocated_quantity;
          });
      }

      // @ts-ignore
      onUpdateRequirement(params);
    }

  }, [activeStep, newData]);

  useEffect(() => {

    const dataTotals: OverViewProps = updateDataTotals(responseOverViewData, newData);

    const newArray = {
      // @ts-ignore
      overview: dataTotals,
      id: stepDataId
    };

    setOverViewData(dataTotals);
    // @ts-ignore
    onUpdateData(newArray, "overview");
  }, [newData]);

  return (
      <>
        <div className={classes.tableContainer}>
          {(activeStep != 5 || (activeStep === 5 && !outJointDisabled)) &&
              (activeStep != 6 || (activeStep === 6 && !aboveMandateAfterFulfillDisabled)) && (
              <>
                {activeStep > 2 && (
                    <>
                      <Typography variant="h4">
                        Overview of allocated volumes from step 2 (advanced sub-mandate)
                      </Typography><br />
                      <PrevTable
                          headCells={allocationPrevTableHeadings}
                          // @ts-ignore
                          rows={stepThreesData}
                          verticalOverflow={true}
                          whiteHeader={true}
                      /><br /><br />
                    </>
                )}

                {activeStep > 3 && (
                    <>
                      <Typography variant="h4">Overview of allocated volumes from step 3 (general mandate)</Typography>
                      <br />
                      <PrevTable
                          headCells={allocationPrevTableHeadings}
                          // @ts-ignore
                          rows={stepFoursData}
                          verticalOverflow={true}
                          whiteHeader={true}
                      /><br /><br />
                    </>
                )}
                {activeStep > 4 && !reqByCustomerDisabled && (
                    <>
                      <Typography variant="h4">
                        Overview of allocated volumes from step 4 (above the mandate required by customers)
                      </Typography><br />
                      <PrevTable
                          headCells={allocationPrevTableHeadings}
                          // @ts-ignore
                          rows={stepFivesData}
                          verticalOverflow={true}
                          whiteHeader={true}
                      /><br /><br />
                    </>
                )}
              </>
          )}

          {activeStep > 5 && !outJointDisabled &&
              (activeStep != 6 || (activeStep === 6 && !aboveMandateAfterFulfillDisabled)) && (
              <>
                <Typography variant="h4">Overview of allocated volumes from step 5 (Joint Fulfillment)</Typography><br/>
                <PrevTable
                    headCells={allocationPrevTableHeadings}
                    // @ts-ignore
                    rows={stepJointFulfsData}
                    verticalOverflow={true}
                    whiteHeader={true}
                /><br /><br />
              </>
          )}
          {activeStep > 6 && !aboveMandateAfterFulfillDisabled && (
              <>
                <Typography variant="h4">Step 6 Data</Typography><br />
                <PrevTable
                    headCells={allocationPrevTableHeadings}
                    // @ts-ignore
                    rows={stepSixsData}
                    verticalOverflow={true}
                    whiteHeader={true}
                /><br /><br />
              </>
          )}
          {activeStep === 1 ? (
              <>
                {!surplusStepDisabled && (
                    <>
                      <Typography variant="h4">Allocation Table</Typography><br/>
                      <SmartTable
                          headCells={allocationSurplusTableHeadings}
                          rows={getData(newData, activeStep)}
                          editableProps={{
                            id: "allocated_quantity",
                            getValue: getAllocated,
                            onChange: handleOnChange,
                            onBlur: handleOnBlur,
                          }}
                          verticalOverflow={true}
                          whiteHeader={true}
                      />
                    </>
                )}
              </>
          ) : (
              <>
                {(activeStep != 5 || (activeStep === 5 && !outJointDisabled)) &&
                    (activeStep != 4 || (activeStep === 4 && !reqByCustomerDisabled)) &&
                    (activeStep != 6 || (activeStep === 6 && !aboveMandateAfterFulfillDisabled)) && (
                        <>
                          <Typography variant="h4">Allocation Table</Typography><br />
                          <SmartTable
                              headCells={allocationTableHeadings}
                              rows={getData(newData, activeStep)}
                              editableProps={{
                                id: "allocated_quantity",
                                getValue: getAllocated,
                                onChange: handleOnChange,
                                onBlur: handleOnBlur,
                              }}
                              verticalOverflow={true}
                              whiteHeader={true}
                          />
                        </>
                    )
                }
              </>
          )}
        </div>
        {(activeStep != 5 || (activeStep === 5 && !outJointDisabled)) &&
            (activeStep != 1 || (activeStep === 1 && !surplusStepDisabled)) &&
            (activeStep != 4 || (activeStep === 4 && !reqByCustomerDisabled)) &&
            (activeStep != 6 || (activeStep === 6 && !aboveMandateAfterFulfillDisabled)) && (
            <OverviewPanel className={classes.overviewPanel}>
              <OverviewTableContainer data={overViewData} step={activeStep} />
            </OverviewPanel>
        )}
      </>
  );
};

// @ts-ignore
export default EditableTable;
